import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Shield, 
  FileSearch, 
  ClipboardCheck, 
  Wrench, 
  Phone, 
  Mail, 
  MapPin, 
  ArrowRight, 
  CheckCircle2, 
  Download, 
  Users, 
  Building2, 
  Star,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// LHS Brand Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/vk7ysv0t_LHS%20LOGO%202%20.png";
const BANNER_URL = "https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/b050mo6q_LHS%20BANNER%201%20%20%281%29.png";
const PDF_GUIDE_URL = "https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/jldk7jd1_The%20Hidden%20Risks%20in%20Park%20Home%20Insurance%20%281%29.pdf";

const services = [
  {
    icon: FileSearch,
    title: "Loss Adjusting",
    description: "Expert assessment and management of insurance claims for park homes, caravans and leisure properties."
  },
  {
    icon: ClipboardCheck,
    title: "Surveying",
    description: "Comprehensive surveys and condition assessments by qualified surveyors with specialist leisure home expertise."
  },
  {
    icon: Shield,
    title: "Damage Inspection Reports",
    description: "Detailed inspection reports documenting damage, causation analysis and repair recommendations."
  },
  {
    icon: Building2,
    title: "Condition Surveys",
    description: "Pre-purchase and periodic condition surveys to protect your investment and identify maintenance needs."
  },
  {
    icon: Wrench,
    title: "Repair Costings & Management",
    description: "Accurate repair estimates and project management to ensure quality repairs at fair prices."
  },
  {
    icon: CheckCircle2,
    title: "Claim Validation",
    description: "Independent validation of insurance claims, supporting fair settlements for all parties."
  }
];

const stats = [
  { value: "500+", label: "Claims Handled" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "24hr", label: "Response Time" },
  { value: "15+", label: "Years Experience" }
];

const testimonials = [
  {
    quote: "LHS handled our storm damage claim professionally and efficiently. The surveyor was thorough and the report helped us get a fair settlement.",
    author: "Park Manager, Wyldecrest Parks",
    rating: 5
  },
  {
    quote: "Excellent service from start to finish. Their expertise in leisure homes made all the difference in our insurance claim.",
    author: "Caravan Owner, Sussex",
    rating: 5
  },
  {
    quote: "We use LHS for all our condition surveys. Their reports are detailed, professional and always delivered on time.",
    author: "Holiday Park Owner, Devon",
    rating: 5
  }
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuideForm, setShowGuideForm] = useState(false);
  const [guideFormData, setGuideFormData] = useState({ name: '', email: '', company: '' });
  const [contactFormData, setContactFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGuideDownload = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_BASE}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: guideFormData.name,
          email: guideFormData.email,
          company: guideFormData.company,
          source: 'Guide Download',
          notes: 'Downloaded: The Hidden Risks in Park Home Insurance PDF'
        })
      });
      
      // Trigger download
      window.open(PDF_GUIDE_URL, '_blank');
      toast.success('Thank you! Your guide is downloading.');
      setShowGuideForm(false);
      setGuideFormData({ name: '', email: '', company: '' });
    } catch (error) {
      // Still allow download even if lead capture fails
      window.open(PDF_GUIDE_URL, '_blank');
      toast.success('Your guide is downloading.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_BASE}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactFormData.name,
          email: contactFormData.email,
          phone: contactFormData.phone,
          source: 'Website Contact Form',
          notes: contactFormData.message
        })
      });
      
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      setContactFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0">
              <img src={LOGO_URL} alt="Leisure Home Solutions" className="h-14 w-auto" />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-stone-600 hover:text-[#1F7A78] transition-colors">Services</a>
              <a href="#about" className="text-stone-600 hover:text-[#1F7A78] transition-colors">About</a>
              <a href="#contact" className="text-stone-600 hover:text-[#1F7A78] transition-colors">Contact</a>
              <div className="flex items-center gap-3 pl-4 border-l border-stone-200">
                <Link to="/client-portal">
                  <Button variant="ghost" size="sm" className="text-[#1F7A78]">Client Portal</Button>
                </Link>
                <Link to="/login">
                  <Button size="sm" className="bg-[#1F7A78] hover:bg-[#005a5c]">Staff Login</Button>
                </Link>
              </div>
            </div>
            
            {/* Mobile menu button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 py-4 px-4 space-y-3">
            <a href="#services" className="block py-2 text-stone-600" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#about" className="block py-2 text-stone-600" onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#contact" className="block py-2 text-stone-600" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Link to="/client-portal" className="block py-2 text-[#1F7A78]">Client Portal</Link>
            <Link to="/login"><Button className="w-full bg-[#1F7A78]">Staff Login</Button></Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={BANNER_URL} 
            alt="Park Homes" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F7A78]/90 to-[#1F7A78]/70" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-2xl">
            <Badge className="mb-6 bg-white/20 text-white border-0 px-4 py-1">
              Specialist Park Home & Leisure Property Services
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Loss Solutions<br />
              <span className="text-[#0dbdad]">Tailored To Your Needs</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
              Expert loss adjusting, surveying and damage inspection services for park homes, 
              caravans and leisure properties across the UK.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-white text-[#1F7A78] hover:bg-stone-100 shadow-lg"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Expert Help
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => setShowGuideForm(true)}
              >
                <Download className="mr-2 h-5 w-5" />
                Free Insurance Guide
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1F7A78] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-[#0dbdad] text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1F7A78]/10 text-[#1F7A78] border-0">Our Services</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Comprehensive Leisure Home Solutions
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              From initial inspection to final settlement, we provide end-to-end support 
              for all your park home and leisure property needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#1F7A78]/10 flex items-center justify-center mb-4 group-hover:bg-[#1F7A78] transition-colors">
                    <service.icon className="h-6 w-6 text-[#1F7A78] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-stone-900 mb-2">{service.title}</h3>
                  <p className="text-stone-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Free Guide */}
      <section className="py-16 bg-gradient-to-br from-[#1F7A78] to-[#005a5c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white max-w-xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Free Guide: The Hidden Risks in Park Home Insurance
              </h2>
              <p className="text-white/80">
                Discover what every park home owner needs to know about insurance claims, 
                coverage gaps and how to protect your investment.
              </p>
            </div>
            <Button 
              size="lg" 
              className="bg-white text-[#1F7A78] hover:bg-stone-100 shadow-lg whitespace-nowrap"
              onClick={() => setShowGuideForm(true)}
            >
              <Download className="mr-2 h-5 w-5" />
              Download Free Guide
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-[#1F7A78]/10 text-[#1F7A78] border-0">About LHS</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                Specialist Expertise in Leisure Home Solutions
              </h2>
              <p className="text-lg text-stone-600 mb-6">
                Leisure Home Solutions brings together decades of experience in loss adjusting, 
                surveying and property inspection specifically focused on the unique needs of 
                park homes, caravans and holiday lodges.
              </p>
              <p className="text-lg text-stone-600 mb-8">
                Our team understands the distinct construction methods, materials and 
                valuation considerations that set leisure properties apart from traditional 
                brick-and-mortar homes.
              </p>
              
              <div className="space-y-4">
                {[
                  "RICS qualified surveyors with leisure property specialism",
                  "Nationwide coverage with 24-hour response",
                  "Independent and impartial assessments",
                  "Digital reporting with photo documentation"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0dbdad] flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-[#1F7A78] rounded-2xl p-8 text-white">
                <h3 className="text-xl font-semibold mb-6">What Our Clients Say</h3>
                <div className="space-y-6">
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-white/10 rounded-xl p-4">
                      <div className="flex gap-1 mb-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-[#0dbdad] text-[#0dbdad]" />
                        ))}
                      </div>
                      <p className="text-white/90 mb-2 text-sm italic">"{testimonial.quote}"</p>
                      <p className="text-[#0dbdad] text-sm font-medium">{testimonial.author}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Badge className="mb-4 bg-[#1F7A78]/10 text-[#1F7A78] border-0">Get In Touch</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                Ready to Discuss Your Needs?
              </h2>
              <p className="text-lg text-stone-600 mb-8">
                Whether you're dealing with a claim, need a survey or want expert advice, 
                our team is here to help. Get in touch for a free initial consultation.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1F7A78]/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-[#1F7A78]" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Call us</p>
                    <a href="tel:07557017694" className="text-lg font-semibold text-stone-900 hover:text-[#1F7A78]">
                      07557 017694
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1F7A78]/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[#1F7A78]" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Email us</p>
                    <a href="mailto:admin@lhsportal.uk" className="text-lg font-semibold text-stone-900 hover:text-[#1F7A78]">
                      admin@lhsportal.uk
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1F7A78]/10 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#1F7A78]" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Coverage</p>
                    <p className="text-lg font-semibold text-stone-900">Nationwide Service</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-stone-900 mb-6">Send Us a Message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Input 
                      placeholder="Your Name *" 
                      required
                      value={contactFormData.name}
                      onChange={(e) => setContactFormData({...contactFormData, name: e.target.value})}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Input 
                      type="email" 
                      placeholder="Email Address *" 
                      required
                      value={contactFormData.email}
                      onChange={(e) => setContactFormData({...contactFormData, email: e.target.value})}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Input 
                      type="tel" 
                      placeholder="Phone Number"
                      value={contactFormData.phone}
                      onChange={(e) => setContactFormData({...contactFormData, phone: e.target.value})}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <textarea 
                      placeholder="How can we help? *" 
                      required
                      rows={4}
                      value={contactFormData.message}
                      onChange={(e) => setContactFormData({...contactFormData, message: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:ring-2 focus:ring-[#1F7A78] focus:border-transparent resize-none"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#1F7A78] hover:bg-[#005a5c]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Portal Links */}
      <section className="py-12 bg-[#1F7A78]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/client-portal" className="group">
              <Card className="bg-white/10 border-0 hover:bg-white/20 transition-colors cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Client Portal</h3>
                    <p className="text-white/70 text-sm">Track your jobs & claims</p>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-white/50 group-hover:text-white transition-colors" />
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/contractor-portal" className="group">
              <Card className="bg-white/10 border-0 hover:bg-white/20 transition-colors cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Contractor Portal</h3>
                    <p className="text-white/70 text-sm">Manage assigned work</p>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-white/50 group-hover:text-white transition-colors" />
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/login" className="group">
              <Card className="bg-white/10 border-0 hover:bg-white/20 transition-colors cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Staff Portal</h3>
                    <p className="text-white/70 text-sm">Internal CRM access</p>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-white/50 group-hover:text-white transition-colors" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="Leisure Home Solutions" className="h-12 w-auto mb-4 brightness-0 invert" />
              <p className="text-stone-400 mb-4 max-w-md">
                Loss Solutions Tailored To Your Needs. Expert services for park homes, 
                caravans and leisure properties across the UK.
              </p>
              <p className="text-stone-500 text-sm">
                © {new Date().getFullYear()} Leisure Home Solutions Ltd. All rights reserved.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-stone-400">
                <li><a href="#services" className="hover:text-white">Loss Adjusting</a></li>
                <li><a href="#services" className="hover:text-white">Surveying</a></li>
                <li><a href="#services" className="hover:text-white">Damage Reports</a></li>
                <li><a href="#services" className="hover:text-white">Condition Surveys</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-stone-400">
                <li><a href="tel:07557017694" className="hover:text-white">07557 017694</a></li>
                <li><a href="mailto:admin@lhsportal.uk" className="hover:text-white">admin@lhsportal.uk</a></li>
                <li className="pt-4">
                  <Link to="/client-portal" className="hover:text-white">Client Portal</Link>
                </li>
                <li>
                  <Link to="/contractor-portal" className="hover:text-white">Contractor Portal</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Guide Download Modal */}
      {showGuideForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Download Free Guide</h3>
                <button onClick={() => setShowGuideForm(false)} className="text-stone-400 hover:text-stone-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-stone-600 mb-6">
                Enter your details to download "The Hidden Risks in Park Home Insurance"
              </p>
              <form onSubmit={handleGuideDownload} className="space-y-4">
                <Input 
                  placeholder="Your Name *" 
                  required
                  value={guideFormData.name}
                  onChange={(e) => setGuideFormData({...guideFormData, name: e.target.value})}
                />
                <Input 
                  type="email" 
                  placeholder="Email Address *" 
                  required
                  value={guideFormData.email}
                  onChange={(e) => setGuideFormData({...guideFormData, email: e.target.value})}
                />
                <Input 
                  placeholder="Company (Optional)"
                  value={guideFormData.company}
                  onChange={(e) => setGuideFormData({...guideFormData, company: e.target.value})}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-[#1F7A78] hover:bg-[#005a5c]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Download Guide'}
                  <Download className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
